import { useCallback, useEffect, useState } from 'react';

import type { InclusionDraft, InclusionDraftPayload, InclusionFormValues } from './types';

export function useInclusionDrafts(offerId: string | undefined) {
  const [drafts, setDrafts] = useState<Array<InclusionDraft>>([]);

  useEffect(() => {
    setDrafts([]);
  }, [offerId]);

  const addDraft = useCallback((values: InclusionFormValues) => {
    const now = new Date().toISOString();
    const draft: InclusionDraft = { ...values, id: crypto.randomUUID(), createdAt: now, updatedAt: now };
    setDrafts((prevDrafts) => [...prevDrafts, draft]);
    return draft;
  }, []);

  const updateDraft = useCallback((id: string, changes: Partial<InclusionFormValues>) => {
    setDrafts((prevDrafts) =>
      prevDrafts.map((draft) =>
        draft.id === id ? { ...draft, ...changes, updatedAt: new Date().toISOString() } : draft,
      ),
    );
  }, []);

  const removeDraft = useCallback((id: string) => {
    setDrafts((prevDrafts) => prevDrafts.filter((draft) => draft.id !== id));
  }, []);

  const replaceOptionDrafts = useCallback((pricingOptionIndex: number, sourceDrafts: Array<InclusionFormValues>) => {
    const now = new Date().toISOString();
    const clones: Array<InclusionDraft> = sourceDrafts.map((draft) => ({
      ...draft,
      id: crypto.randomUUID(),
      pricingOptionIndex,
      createdAt: now,
      updatedAt: now,
    }));
    setDrafts((prevDrafts) => [
      ...prevDrafts.filter((draft) => draft.pricingOptionIndex !== pricingOptionIndex),
      ...clones,
    ]);
  }, []);

  const loadDraft = useCallback((savedDrafts: Array<InclusionDraftPayload>) => {
    const now = new Date().toISOString();
    setDrafts(savedDrafts.map((draft) => ({ ...draft, createdAt: now, updatedAt: now })));
  }, []);

  return { drafts, addDraft, updateDraft, removeDraft, replaceOptionDrafts, loadDraft };
}
